/**
 * AppLayout — Shell global CROSS M3 (F3 fiel ao prototipo aprovado).
 *
 * Estrutura:
 *   .cross-app  (grid 2 colunas: rail + main)
 *     nav.nav-rail       — Navigation Rail 80px
 *     .cross-main
 *       header.topbar    — Topbar sticky 108px
 *       main             — Conteudo da rota (via Outlet)
 *
 * Regras de dado honesto:
 *   - Saudacao: periodo real pelo relogio local.
 *   - Nome: derivado do email (antes do @) ou campo name se disponivel.
 *   - Frase proativa: numero real de alertas do radar se disponivel; senao
 *     linha neutra — nunca inventada.
 *   - Chip de freshness: omitido (sem timestamp de coleta disponivel no shell
 *     sem chamada adicional — follow-up para expor lastSync no /me ou cache
 *     global de coleta).
 *   - Seletores de periodo/fonte: leem e escrevem o FilterContext global
 *     (provido pelo FilterProvider em App.tsx). Trocar aqui atualiza os dados
 *     em todas as paginas que consomem useFilterContext().
 */

import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import {
  useFilterContext,
  type PeriodMode as FilterPeriodMode,
  type SourceFilter,
} from '../lib/filter-context';
import { t, type TranslationKey } from '../lib/i18n';
import { useAuth } from '../lib/auth-context';
import { TenantSwitcher } from './TenantSwitcher';
import { MfaGate } from './MfaGate';

interface NavItem {
  readonly to: string;
  readonly labelKey: TranslationKey;
  readonly icon: string;
}

interface NavGroup {
  readonly groupLabelKey: TranslationKey;
  readonly items: readonly NavItem[];
}

/**
 * Novo rail em 3 grupos (D-050 + IA-NAV-PROP-01..10).
 * Ordem: Operacao Diaria → Analise → Administracao.
 * Separadores com rotulo caixa-alta (Opcao A recomendada pelo Product-Designer — D-054).
 */
const NAV_GROUPS: readonly NavGroup[] = [
  {
    groupLabelKey: 'nav.groupOperation',
    items: [
      { to: '/mirror', labelKey: 'nav.mirror', icon: 'home_app_logo' },
      { to: '/chat', labelKey: 'nav.chat', icon: 'forum' },
      { to: '/reports', labelKey: 'nav.report', icon: 'summarize' },
      { to: '/insights', labelKey: 'nav.insights', icon: 'lightbulb' },
      { to: '/radar', labelKey: 'nav.radar', icon: 'monitor_heart' },
    ],
  },
  {
    groupLabelKey: 'nav.groupAnalysis',
    items: [
      { to: '/dashboard', labelKey: 'nav.fourPs', icon: 'bar_chart' },
      { to: '/digital', labelKey: 'nav.digital', icon: 'language' },
    ],
  },
  {
    groupLabelKey: 'nav.groupAdmin',
    items: [
      { to: '/sources', labelKey: 'nav.sources', icon: 'hub' },
      { to: '/billing', labelKey: 'nav.billing', icon: 'credit_card' },
    ],
  },
];

/** Item de configuracoes — separado no rodape pelo spacer. */
const SETTINGS_ITEM: NavItem = {
  to: '/settings',
  labelKey: 'nav.settingsShort',
  icon: 'settings',
};

/** Deriva o primeiro nome de um email (antes do @ e do primeiro ponto/hifen). */
function firstNameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? '';
  // Remove numeros, separa por ponto/hifen/underscore, capitaliza primeiro segmento
  const segments = local.split(/[.\-_]/);
  const first = segments[0] ?? '';
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
}

/** Retorna chave de saudacao de acordo com hora local. */
function greetingKey(hour: number): TranslationKey {
  if (hour >= 6 && hour < 12) return 'topbar.greetingMorning';
  if (hour >= 12 && hour < 18) return 'topbar.greetingAfternoon';
  return 'topbar.greetingEvening';
}

/** Deriva as iniciais para o avatar (ate 2 chars). */
function avatarInitials(email: string | null): string {
  if (email === null || email.length === 0) return '?';
  const name = firstNameFromEmail(email);
  return name.slice(0, 2).toUpperCase();
}

/** SVG da marca CROSS (mesmo vetor do prototipo aprovado). */
function CrossLogo(): JSX.Element {
  return (
    <svg
      viewBox="0 0 1080 1080"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ width: 32, height: 32, display: 'block' }}
    >
      <path
        fill="#F9005C"
        d="M139.17,221.92v319.65l403.53-233.36-276.21-159.73c-56.56-32.71-127.32,8.11-127.32,73.45Z"
      />
      <path
        fill="#F9005C"
        d="M266.49,928.41l270.81-156.61-398.13-230.24v313.4c0,65.34,70.76,106.16,127.32,73.45Z"
      />
      <path
        fill="#0079DC"
        d="M940.83,858.08v-319.65l-403.53,233.36,276.21,159.73c56.56,32.71,127.32-8.11,127.32-73.45Z"
      />
      <path
        fill="#0079DC"
        d="M542.7,308.2l398.13,230.24V225.04c0-65.34-70.76-106.16-127.32-73.45l-270.81,156.61Z"
      />
      <polygon
        fill="#3F2570"
        points="139.17 541.56 537.3 771.8 940.83 538.44 542.7 308.2 139.17 541.56"
      />
    </svg>
  );
}

/**
 * Rotas onde os seletores de periodo/fonte fazem sentido semantico.
 * Em /settings, /billing, /chat, /sources e rotas internas: topbar-right limpo.
 */
const ROUTES_WITH_FILTERS = new Set(['/dashboard', '/mirror', '/radar', '/insights']);

/**
 * Prefixos de rota onde o seletor de periodo aparece mas o seletor de fonte
 * e omitido — cada aba da area Digital ja fixa a fonte relevante.
 */
const ROUTES_PERIOD_ONLY_PREFIXES = ['/digital'];

/**
 * Chip de acao contextual por rota — conforme prototipo.
 * Retorna null quando a rota nao tem chip de acao definido.
 *
 * Copy inline (nao via i18n.ts per instrucao F3 — adicionar chave topbar.chipChatCross
 * ao i18n em follow-up quando Content-Designer revisar o microcopy).
 * FLAG: TOPBAR_CHIP_I18N — mover string para i18n.ts + t() apos revisao de copy.
 */
function routeActionChip(pathname: string, onNavigate: (to: string) => void): JSX.Element | null {
  // Dashboard/Mirror/Radar/Insights: chip "Conversar com a CROSS"
  if (
    pathname === '/radar' ||
    pathname === '/dashboard' ||
    pathname === '/mirror' ||
    pathname === '/insights'
  ) {
    return (
      <button
        type="button"
        className="topbar-action-chip"
        onClick={() => onNavigate('/chat')}
        aria-label={t('chat.topbarTitle')}
      >
        <span className="material-symbols-rounded topbar-action-chip-icon" aria-hidden="true">
          forum
        </span>
        {t('chat.topbarTitle')}
      </button>
    );
  }
  return null;
}

export function AppLayout(): JSX.Element {
  const {
    logout,
    email,
    name: authName,
    memberships,
    activeTenantId,
    forbiddenFlash,
    clearForbidden,
    lostTenantFlash,
    clearLostTenant,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Filtros globais — lidos e escritos via FilterContext
  const {
    periodMode,
    selectedYear,
    selectedMonth,
    source,
    onChangePeriod,
    onChangeYear,
    onChangeMonth,
    onApply,
    onChangeSource,
  } = useFilterContext();

  const handleLogout = useCallback((): void => {
    logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  const handleNavigate = useCallback(
    (to: string): void => {
      navigate(to);
    },
    [navigate],
  );

  // --- Saudacao honesta ---
  const greeting = useMemo((): string => {
    const hour = new Date().getHours();
    const key = greetingKey(hour);
    // Nome real do /me quando disponivel (primeiro nome); senao deriva do email.
    const firstName = (s: string): string => s.trim().split(/\s+/)[0] ?? s;
    const name =
      authName !== null && authName.length > 0
        ? firstName(authName)
        : email !== null
          ? firstNameFromEmail(email)
          : 'voce';
    return t(key, { name });
  }, [authName, email]);

  // Frase proativa: neutra — sem dado real disponivel no shell.
  // FLAG: TOPBAR_PROACTIVE_ALERTS — quando um endpoint /me expor alertCount,
  // substituir por: t('topbar.proactiveAlerts', { count: String(n) })
  const proactive = t('topbar.proactiveNeutral');

  // Iniciais do avatar
  const initials = useMemo(() => avatarInitials(email), [email]);

  // Nome do tenant ativo (para o avatar title)
  const activeTenant = useMemo(
    () => memberships.find((m) => m.tenant_id === activeTenantId),
    [memberships, activeTenantId],
  );
  const avatarTitle = activeTenant?.tenant_name ?? email ?? t('topbar.avatarAriaLabel');

  // Topbar condicional: seletores de periodo/fonte so aparecem em rotas relevantes.
  const showFilters = ROUTES_WITH_FILTERS.has(location.pathname);

  // Area Digital: periodo visivel, fonte omitida (cada aba fixa a propria fonte).
  const showPeriodOnly =
    !showFilters &&
    ROUTES_PERIOD_ONLY_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));

  // Chip de acao contextual por rota
  const actionChip = routeActionChip(location.pathname, handleNavigate);

  return (
    <div className="cross-app">
      {/* ===== NAVIGATION RAIL ===== */}
      <nav className="nav-rail" aria-label={t('nav.primaryNav')}>
        {/* Marca CROSS */}
        <NavLink
          to="/mirror"
          className="rail-brand"
          aria-label={t('topbar.brandAriaLabel')}
          title={t('app.brandName')}
        >
          <CrossLogo />
        </NavLink>

        {/* Itens em grupos com separadores (D-050 + IA-NAV-PROP) */}
        {NAV_GROUPS.map((group) => (
          <div key={group.groupLabelKey} className="rail-group">
            <div className="rail-group-label" aria-hidden="true">
              {t(group.groupLabelKey)}
            </div>
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => (isActive ? 'rail-item active' : 'rail-item')}
                aria-label={t(item.labelKey)}
              >
                <div className="rail-item-indicator">
                  <span className="rail-icon material-symbols-rounded" aria-hidden="true">
                    {item.icon}
                  </span>
                </div>
                <span className="rail-label">{t(item.labelKey)}</span>
              </NavLink>
            ))}
          </div>
        ))}

        {/* Spacer empurra configuracoes + avatar para o rodape */}
        <div className="rail-spacer" aria-hidden="true" />

        {/* Configuracoes no rodape */}
        <NavLink
          to={SETTINGS_ITEM.to}
          className={({ isActive }) => (isActive ? 'rail-item active' : 'rail-item')}
          aria-label={t(SETTINGS_ITEM.labelKey)}
        >
          <div className="rail-item-indicator">
            <span className="rail-icon material-symbols-rounded" aria-hidden="true">
              {SETTINGS_ITEM.icon}
            </span>
          </div>
          <span className="rail-label">{t(SETTINGS_ITEM.labelKey)}</span>
        </NavLink>

        {/*
         * Avatar no rodape do rail.
         * O TenantSwitcher gerencia a troca de tenant — fica escondido do
         * rail visual mas permanece acessivel via keyboard.
         * O div.rail-avatar e puramente visual (aria-hidden) — as iniciais
         * sao decorativas; a acao esta no TenantSwitcher abaixo.
         */}
        <div className="rail-footer">
          <div className="rail-avatar" aria-hidden="true" title={avatarTitle}>
            {initials}
          </div>
          <div className="rail-tenant-switcher">
            <TenantSwitcher />
          </div>
        </div>
      </nav>

      {/* ===== MAIN ===== */}
      <div className="cross-main">
        {/* Flash messages — acima da topbar para maximo contraste */}
        {forbiddenFlash !== null ? (
          <div className="flash flash-error" role="alert">
            {t('common.forbidden')}
            <button type="button" onClick={clearForbidden} aria-label={t('common.back')}>
              x
            </button>
          </div>
        ) : null}
        {lostTenantFlash ? (
          <div className="flash flash-warning" role="alert">
            {t('tenants.switcher.lostAccess')}
            <button type="button" onClick={clearLostTenant} aria-label={t('common.back')}>
              x
            </button>
          </div>
        ) : null}

        {/* Topbar */}
        <header className="topbar" role="banner">
          <div className="topbar-left">
            <div className="topbar-greet-block">
              <div className="topbar-greeting" aria-live="polite">
                {greeting}
              </div>
              <div className="topbar-proactive" aria-live="polite">
                {proactive}
              </div>
            </div>
          </div>

          <div className="topbar-right">
            {/*
             * FLAG: TOPBAR_FRESHNESS — omitido pois lastSync nao esta disponivel
             * no shell sem chamada adicional. Reativar quando GET /me (ou cache
             * global de coleta) expor lastSync.
             */}

            {/* Chip de acao contextual por rota (ex.: "Conversar com a CROSS" no Radar/Painel) */}
            {actionChip}

            {/*
             * Seletores de periodo/fonte: aparecem apenas em rotas onde fazem sentido
             * semantico (dashboard, mirror, radar, insights).
             * Em /settings, /billing, /chat, /sources: topbar-right fica limpo.
             * Em /digital e subrotas: so o seletor de periodo — sem seletor de fonte
             * (cada aba fixa a propria fonte; exibir seletor de fonte conflitaria).
             * Leem e escrevem o FilterContext global — trocar aqui atualiza os dados
             * em todas as paginas filhas que consomem useFilterContext().
             */}
            {showFilters || showPeriodOnly ? (
              <>
                <div className="topbar-sep" aria-hidden="true" />

                {/* Seletor de periodo */}
                <div className="sel-wrapper">
                  <span className="sel-icon material-symbols-rounded" aria-hidden="true">
                    calendar_month
                  </span>
                  <select
                    id="topbar-period-select"
                    className="sel"
                    aria-label={t('topbar.periodAriaLabel')}
                    value={periodMode}
                    onChange={(e) => onChangePeriod(e.target.value as FilterPeriodMode)}
                  >
                    <option value="30d">{t('topbar.period30d')}</option>
                    <option value="7d">{t('topbar.period7d')}</option>
                    <option value="90d">{t('topbar.period90d')}</option>
                    <option value="365d">{t('topbar.periodYear')}</option>
                    <option value="month">{t('fourPs.period.specificMonth')}</option>
                  </select>
                  <span className="sel-arrow material-symbols-rounded" aria-hidden="true">
                    expand_more
                  </span>
                </div>

                {/* Seletores de mes/ano — so em modo 'month' */}
                {periodMode === 'month' ? (
                  <>
                    <div className="sel-wrapper">
                      <select
                        className="sel"
                        aria-label={t('fourPs.monthFilter.monthLabel')}
                        value={selectedMonth}
                        onChange={(e) => onChangeMonth(Number(e.target.value))}
                      >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                          <option key={m} value={m}>
                            {t(`fourPs.monthFilter.months.m${m}` as 'fourPs.monthFilter.months.m1')}
                          </option>
                        ))}
                      </select>
                      <span className="sel-arrow material-symbols-rounded" aria-hidden="true">
                        expand_more
                      </span>
                    </div>

                    <div className="sel-wrapper">
                      <select
                        className="sel"
                        aria-label={t('fourPs.monthFilter.yearLabel')}
                        value={selectedYear}
                        onChange={(e) => onChangeYear(Number(e.target.value))}
                      >
                        {Array.from(
                          { length: Math.max(1, new Date().getFullYear() - 2024) },
                          (_, i) => 2025 + i,
                        ).map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                      <span className="sel-arrow material-symbols-rounded" aria-hidden="true">
                        expand_more
                      </span>
                    </div>

                    <button type="button" className="topbar-apply-btn" onClick={onApply}>
                      {t('fourPs.monthFilter.apply')}
                    </button>
                  </>
                ) : null}

                {/* Seletor de fonte — omitido na area Digital (fonte fixada pela aba) */}
                {showFilters ? (
                  <div className="sel-wrapper">
                    <span className="sel-icon material-symbols-rounded" aria-hidden="true">
                      tune
                    </span>
                    <select
                      className="sel"
                      aria-label={t('topbar.sourceAriaLabel')}
                      value={source}
                      onChange={(e) => onChangeSource(e.target.value as SourceFilter)}
                    >
                      <option value="all">{t('topbar.sourceAll')}</option>
                      <option value="instagram">{t('topbar.sourceInstagram')}</option>
                      <option value="meta_ads">{t('topbar.sourceMetaAds')}</option>
                      <option value="google_drive">{t('topbar.sourceDrive')}</option>
                    </select>
                    <span className="sel-arrow material-symbols-rounded" aria-hidden="true">
                      expand_more
                    </span>
                  </div>
                ) : null}
              </>
            ) : null}

            {/* Logout — acao de seguranca, mantida acessivel no topbar */}
            <button
              type="button"
              className="topbar-logout"
              onClick={handleLogout}
              aria-label={t('nav.logout')}
              title={t('nav.logout')}
            >
              <span className="material-symbols-rounded" aria-hidden="true">
                logout
              </span>
            </button>
          </div>
        </header>

        <MfaGate>
          <main className="cross-content" role="main">
            <Outlet />
          </main>
        </MfaGate>
      </div>
    </div>
  );
}
